import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { MessageCircle, Heart } from "lucide-react";

import LayoutEngine from "../ui/LayoutEngine";
import PageHeader from "../layout/PageHeader";
import { loadScreen } from "../runtime/loader";
import { setScreen, getS, getScreen, setS, sub, broadcastList, clearSessionScopedUI } from "../core/state";
import { setV } from "../core/binding";
import { runRules } from "../core/ruleEngine";
import { validate } from "../core/validation";
import {
  getAuthUser,
  getCurrentUserRole,
  getDefaultRouteForRole,
  getAccountProfile,
  hasPasskeyForEmail,
  isAuthenticated,
  login,
  loginWithPasskey,
  removePasskeyForEmail,
  registerPasskeyForEmail,
  signup,
  updateAccountProfile,
} from "../core/auth";
import { isRoleAllowed } from "../core/access";
import { useHeaderNav, findHeaderTopNav } from "../context/HeaderNavContext";
import { addToCartLine, clearCartStorage } from "../core/cart";
import { CATALOG_UPDATED_EVENT, notifyCatalogUpdated } from "../core/catalogSync";
import { normalizeProductType, normalizeSpecObject } from "../core/productModel";

const parseColorImageMap = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return {};
    }
  }
  return {};
};

const parseVariantGalleryMap = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  return {};
};

const VARIANT_SHOT_ORDER = ["front", "draped", "pallu", "border", "texture", "back", "zoom"];
const toBool = (v) => {
  if (v === true || v === 1) return true;
  const s = String(v ?? "").trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes" || s === "on";
};
const SAMPLE_PRODUCT_CDN = {
  id: 900001,
  seq: 1,
  name: "Ruby Banarasi Silk Saree",
  category: "Banarasi Saree",
  price: 8999,
  discountPrice: 6299,
  brand: "NISTTA",
  occasion: "Wedding / Bridal",
  festival: "Diwali",
  fabric: "Silk",
  mainColor: "Red",
  workType: "Zari",
  pattern: "Traditional Motifs",
  smartTag: "Bridal Picks",
  availableColors: "Red, Blue",
  description: "Pure Banarasi silk saree with rich zari weave, festive drape flow, and statement pallu.",
  images: [
    "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1610041321327-b794da1f03ea?auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1610030347203-5a5f93d3536f?auto=format&fit=crop&w=1200&q=80",
  ],
  colorImageMap: {
    Red: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=900&q=80",
    Blue: "https://images.unsplash.com/photo-1610030347203-5a5f93d3536f?auto=format&fit=crop&w=900&q=80",
  },
  variantGalleryMap: {
    Red: {
      front: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=1200&q=80",
      draped: "https://images.unsplash.com/photo-1610041321327-b794da1f03ea?auto=format&fit=crop&w=1200&q=80",
      pallu: "https://images.unsplash.com/photo-1610030347203-5a5f93d3536f?auto=format&fit=crop&w=1200&q=80",
      border: "https://images.unsplash.com/photo-1610030347203-5a5f93d3536f?auto=format&fit=crop&w=800&q=80",
      texture: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=800&q=80",
      back: "https://images.unsplash.com/photo-1610041321327-b794da1f03ea?auto=format&fit=crop&w=1000&q=80",
    },
    Blue: {
      front: "https://images.unsplash.com/photo-1610030347203-5a5f93d3536f?auto=format&fit=crop&w=1200&q=80",
      draped: "https://images.unsplash.com/photo-1610041321327-b794da1f03ea?auto=format&fit=crop&w=1200&q=80",
      pallu: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=1200&q=80",
      border: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=800&q=80",
      texture: "https://images.unsplash.com/photo-1610030347203-5a5f93d3536f?auto=format&fit=crop&w=800&q=80",
      back: "https://images.unsplash.com/photo-1610041321327-b794da1f03ea?auto=format&fit=crop&w=1000&q=80",
    },
  },
};

const deriveFromVariantGalleryMap = (variantMap) => {
  const entries = Object.entries(variantMap || {}).filter(([k, v]) => k && v && typeof v === "object");
  if (!entries.length) return { availableColors: [], colorImageMap: {}, images: [] };

  const allImages = [];
  const colorImageMap = {};
  const availableColors = entries.map(([color]) => color);
  entries.forEach(([color, shots]) => {
    const shotUrls = [];
    VARIANT_SHOT_ORDER.forEach((shot) => {
      const url = typeof shots?.[shot] === "string" ? shots[shot].trim() : "";
      if (url) shotUrls.push(url);
    });
    if (!shotUrls.length) {
      Object.values(shots || {}).forEach((v) => {
        if (typeof v === "string" && v.trim()) shotUrls.push(v.trim());
      });
    }
    if (shotUrls.length) {
      colorImageMap[color] = shotUrls[0];
      allImages.push(...shotUrls);
    }
  });

  return {
    availableColors,
    colorImageMap,
    images: [...new Set(allImages)],
  };
};

const normalizeCatalogItem = (item, idx = 0) => {
  const sku = String(item?.sku || item?.id || "").trim();
  return {
    ...item,
    id: item?.id ?? (sku || Date.now() + idx),
    sku,
    seq: item?.seq != null && item?.seq !== "" ? Number(item.seq) : idx + 1,
    productType: normalizeProductType(item?.productType),
    spec: normalizeSpecObject(item?.spec),
  };
};

const hasNonEmptyImages = (images) =>
  Array.isArray(images) && images.some((u) => typeof u === "string" && u.trim().length > 0);

const hasPlainMap = (m) => m && typeof m === "object" && !Array.isArray(m) && Object.keys(m).length > 0;

/** Avoid wiping demo/base media when the server row omits images/maps (incomplete merge). */
const mergeBaseRowWithUpload = (baseRow, uploadRow) => {
  const out = { ...baseRow, ...uploadRow };
  if (!hasNonEmptyImages(uploadRow?.images) && hasNonEmptyImages(baseRow?.images)) {
    out.images = baseRow.images;
  }
  if (!hasPlainMap(uploadRow?.colorImageMap) && hasPlainMap(baseRow?.colorImageMap)) {
    out.colorImageMap = baseRow.colorImageMap;
  }
  if (!hasPlainMap(uploadRow?.variantGalleryMap) && hasPlainMap(baseRow?.variantGalleryMap)) {
    out.variantGalleryMap = baseRow.variantGalleryMap;
  }
  if (!String(uploadRow?.availableColors ?? "").trim() && String(baseRow?.availableColors ?? "").trim()) {
    out.availableColors = baseRow.availableColors;
  }
  if (!String(uploadRow?.name ?? "").trim() && String(baseRow?.name ?? "").trim()) {
    out.name = baseRow.name;
  }
  if (!String(uploadRow?.description ?? "").trim() && String(baseRow?.description ?? "").trim()) {
    out.description = baseRow.description;
  }
  if (!String(uploadRow?.productType ?? "").trim() && String(baseRow?.productType ?? "").trim()) {
    out.productType = baseRow.productType;
  }
  if (!hasPlainMap(uploadRow?.spec) && hasPlainMap(baseRow?.spec)) {
    out.spec = baseRow.spec;
  }
  return out;
};

const mergeCatalogLists = (baseList, uploadedList) => {
  const merged = new Map();
  (Array.isArray(baseList) ? baseList : []).forEach((row, idx) => {
    const normalized = normalizeCatalogItem(row, idx);
    const key = String(normalized.sku || normalized.id || `base-${idx}`).toLowerCase();
    merged.set(key, normalized);
  });
  (Array.isArray(uploadedList) ? uploadedList : []).forEach((row, idx) => {
    const normalized = normalizeCatalogItem(row, idx);
    const key = String(normalized.sku || normalized.id || `upload-${idx}`).toLowerCase();
    const prev = merged.get(key) || {};
    merged.set(key, mergeBaseRowWithUpload(prev, normalized));
  });
  return Array.from(merged.values()).map((row, idx) => ({ ...row, seq: idx + 1 }));
};

const withVersionParam = (url) => {
  const raw = String(url || "").trim();
  if (!raw) return raw;
  const sep = raw.includes("?") ? "&" : "?";
  return `${raw}${sep}v=${Date.now()}`;
};

const PRODUCT_VIEWS_KEY = "lfx_product_views";
const readProductViews = () => {
  try {
    const raw = localStorage.getItem(PRODUCT_VIEWS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch (e) {
    return {};
  }
};
const writeProductViews = (map) => {
  try {
    localStorage.setItem(PRODUCT_VIEWS_KEY, JSON.stringify(map || {}));
  } catch (e) {
    // ignore storage failures
  }
};
const getProductViewToken = (item) => {
  const sku = String(item?.sku || "").trim().toLowerCase();
  if (sku) return `sku:${sku}`;
  const id = String(item?.id || "").trim().toLowerCase();
  if (id) return `id:${id}`;
  return "";
};
const getProductViewCount = (item) => {
  const token = getProductViewToken(item);
  const base = Number(item?.views);
  if (!token) return Number.isFinite(base) ? base : 0;
  const map = readProductViews();
  const tracked = Number(map[token]);
  if (Number.isFinite(tracked)) return tracked;
  return Number.isFinite(base) ? base : 0;
};
const incrementProductViewCount = (item) => {
  const token = getProductViewToken(item);
  if (!token) return 0;
  const map = readProductViews();
  const current = Number(map[token]);
  const next = (Number.isFinite(current) ? current : 0) + 1;
  map[token] = next;
  writeProductViews(map);
  return next;
};

/** Refetch demo JSON + server uploads and broadcast (no browser HTTP cache). */
const fetchAndBroadcastMergedCatalog = async (scr, moduleName = "") => {
  const url = scr?.defaultCatalog;
  if (!url || typeof url !== "string" || !url.trim()) return;
  const isAdminCatalog = moduleName === "adminProducts";
  setS({ isCatalogLoading: true });
  const catalogVer = Math.max(0, Number(scr?.demoCatalogVersion) || 0);
  let appliedVer = 0;
  try {
    appliedVer = parseInt(localStorage.getItem("lfx_demo_catalog_v") || "0", 10) || 0;
  } catch (e) {
    appliedVer = 0;
  }
  try {
    const res = await fetch(withVersionParam(url), { cache: "no-store" });
    if (!res.ok) {
      setS({ isCatalogLoading: false });
      return;
    }
    const data = await res.json();
    let baseList = Array.isArray(data) ? data : data?.list;
    if (!Array.isArray(baseList)) baseList = [];
    if (isAdminCatalog) baseList = [];
    let uploadedList = [];
    let deletedSkus = [];
    let vendorCatalogFetchFailed = false;
    const authUser = getAuthUser();
    const vendorId = String(authUser?.email || authUser?.name || "").trim();
    try {
      const catalogUrl = isAdminCatalog && vendorId
        ? `/api/product-media/catalog?vendorId=${encodeURIComponent(vendorId)}`
        : "/api/product-media/catalog";
      const uploadedRes = await fetch(withVersionParam(catalogUrl), { cache: "no-store" });
      if (uploadedRes.ok) {
        const uploadedData = await uploadedRes.json();
        uploadedList = Array.isArray(uploadedData?.list) ? uploadedData.list : [];
        deletedSkus = Array.isArray(uploadedData?.deletedSkus) ? uploadedData.deletedSkus : [];
      } else if (isAdminCatalog && vendorId) {
        vendorCatalogFetchFailed = true;
      }
    } catch (e) {
      if (isAdminCatalog && vendorId) vendorCatalogFetchFailed = true;
    }
    if (vendorCatalogFetchFailed) {
      const cur = getS().list;
      if (Array.isArray(cur) && cur.length > 0) {
        setS({ isCatalogLoading: false });
        return;
      }
    }
    if (deletedSkus.length) {
      const deletedSet = new Set(deletedSkus.map((s) => String(s || "").trim().toLowerCase()).filter(Boolean));
      baseList = baseList.filter((row) => !deletedSet.has(String(row?.sku || row?.id || "").trim().toLowerCase()));
      uploadedList = uploadedList.filter((row) => !deletedSet.has(String(row?.sku || row?.id || "").trim().toLowerCase()));
    }
    const withSeq = mergeCatalogLists(baseList, uploadedList);
    broadcastList(withSeq);
    if (catalogVer > 0 && catalogVer > appliedVer) {
      try {
        localStorage.setItem("lfx_demo_catalog_v", String(catalogVer));
      } catch (e) {
        /* ignore */
      }
    }
    setS({ isCatalogLoading: false });
  } catch (e) {
    /* demo catalog optional */
    setS({ isCatalogLoading: false });
  }
};

const buildGalleryImages = (item) => {
  const urls = [];
  const append = (value) => {
    if (Array.isArray(value)) {
      value.filter(Boolean).forEach(append);
      return;
    }
    if (typeof value === "string" && value.trim()) {
      const trimmed = value.trim();
      if (trimmed.startsWith("data:image/")) {
        urls.push(trimmed);
        return;
      }
      if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            parsed.filter(Boolean).forEach(append);
            return;
          }
        } catch (e) {
          // fall through to string parsing
        }
      }
      if (trimmed.includes(",") && !trimmed.startsWith("data:")) {
        value.split(",").map((v) => v.trim()).filter(Boolean).forEach(append);
      } else {
        urls.push(trimmed);
      }
    }
  };

  append(item?.images2);
  append(item?.images);
  append(item?.image);
  append(item?.thumbnail);
  append(item?.thumbnailUrl);
  append(item?.mainImage);
  const variantMap = parseVariantGalleryMap(item?.variantGalleryMap);
  const fromVariant = deriveFromVariantGalleryMap(variantMap);
  append(fromVariant.images);
  return [...new Set(urls)];
};

const parseColorImageMapForCart = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
      return {};
    }
  }
  return {};
};

const SERVER_CATALOG_ITEM_OMIT = new Set([
  "id",
  "seq",
  "zipImport",
  "multiImageImport",
  "galleryImages",
  "specHeading",
  "productJson",
  "__structuredImport",
  "outOfStock",
  "stock",
]);

const persistCatalogItemToServer = (item) => {
  const sku = String(item?.sku || "").trim();
  if (!sku) return Promise.resolve();
  const authUser = getAuthUser();
  const vendorId = String(item?.vendorId || authUser?.email || authUser?.name || "").trim();
  const payload = {};
  Object.keys(item || {}).forEach((k) => {
    if (SERVER_CATALOG_ITEM_OMIT.has(k)) return;
    payload[k] = item[k];
  });
  payload.sku = sku;
  if (vendorId) payload.vendorId = vendorId;
  return fetch("/api/product-media/catalog-item", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }).then(async (res) => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.message || `Save failed (${res.status})`);
    return body;
  });
};

const deleteCatalogItemFromServer = (sku) => {
  const normalized = String(sku || "").trim();
  if (!normalized) return Promise.resolve();
  const authUser = getAuthUser();
  const vendorId = String(authUser?.email || authUser?.name || "").trim();
  const qs = vendorId ? `?vendorId=${encodeURIComponent(vendorId)}` : "";
  return fetch(`/api/product-media/catalog-item/${encodeURIComponent(normalized)}${qs}`, {
    method: "DELETE",
  }).then(async (res) => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.message || `Delete failed (${res.status})`);
    return body;
  });
};

const firstColorFromAvailable = (ac) => {
  if (!ac) return "";
  if (Array.isArray(ac)) return String(ac[0] || "").trim();
  return String(ac).split(",")[0].trim();
};

const pickCartImage = (form, color) => {
  const vgm = parseVariantGalleryMap(form?.variantGalleryMap);
  if (color && vgm[color] && typeof vgm[color] === "object") {
    const shots = vgm[color];
    const front = typeof shots.front === "string" ? shots.front.trim() : "";
    if (front) return front;
    const first = Object.values(shots).find((u) => typeof u === "string" && u.trim());
    if (first) return String(first).trim();
  }
  const cmap = parseColorImageMapForCart(form?.colorImageMap);
  if (color && cmap[color]) return String(cmap[color]).trim();
  const imgs = buildGalleryImages(form || {});
  return imgs[0] || "";
};


/* =============================================
   GENERIC SCREEN — universal JSON-driven screen
   ============================================= */
export default function GenericScreen() {
  const { module, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const activeModule = module || (location.pathname === "/login" ? "login" : "");
  useEffect(() => {
    if (activeModule !== "productDetail") return;
    if (String(id || "").trim()) return;
    navigate("/products", { replace: true });
  }, [activeModule, id, navigate]);
  const [screen, setScr] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [rev, refresh] = useState(0);
  const lastViewedTokenRef = useRef("");
  const { setHeaderNav } = useHeaderNav();
  const WA_PHONE = "919866287228";
  const FAVORITES_KEY = "lfx_favorites";
  const readFavoriteIds = () => {
    try {
      const raw = localStorage.getItem(FAVORITES_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map((x) => String(x)) : [];
    } catch (e) {
      return [];
    }
  };
  const writeFavoriteIds = (ids) => {
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids || []));
    } catch (e) {
      // ignore storage issues
    }
  };
  const formatInr = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return `Rs. ${n.toLocaleString("en-IN")}`;
  };
  const buildWhatsappUrl = () => {
    const form = getS()?.form || {};
    const title = form.name || "Saree";
    const offer = formatInr(form.discountPrice) || "N/A";
    const mrp = formatInr(form.price) || "N/A";
    const page = typeof window !== "undefined" ? window.location.href : "";
    const text = `Hi, I want to enquire about this product:%0A${title}%0AOffer: ${offer}%0AMRP: ${mrp}${page ? `%0A${page}` : ""}`;
    return `https://wa.me/${WA_PHONE}?text=${text}`;
  };
  const findDetailItemFromLists = (sourceModule, routeId, idKey = "id") => {
    const routeRaw = String(routeId || "").trim();
    const routeLc = routeRaw.toLowerCase();
    if (!routeRaw) return null;
    const navItem = location.state?.detailItem;
    if (navItem && typeof navItem === "object") {
      const nid = String(navItem.id ?? "").trim();
      const nsku = String(navItem.sku ?? "").trim();
      if (nid === routeRaw || nsku.toLowerCase() === routeLc) return navItem;
    }
    const sourceList = Array.isArray(getScreen(sourceModule)?.list) ? getScreen(sourceModule).list : [];
    const currentList = Array.isArray(getS()?.list) ? getS().list : [];
    const merged = [...sourceList, ...currentList];
    return (
      merged.find((item) => {
        const byConfiguredKey = String(item?.[idKey] ?? "").trim();
        const byId = String(item?.id ?? "").trim();
        const bySku = String(item?.sku ?? "").trim();
        return (
          byConfiguredKey === routeRaw
          || byId === routeRaw
          || bySku.toLowerCase() === routeLc
        );
      }) || null
    );
  };
  const buildBreadcrumbs = () => {
    const inferParentPath = (parentLabel) => {
      const p = String(parentLabel || "").trim().toLowerCase();
      if (p === "shop") return "/products";
      return null;
    };
    const crumbs = [{ label: "Home", path: "/" }];
    const parent = screen?.menu?.parent;
    const parentPath = screen?.menu?.parentPath ?? inferParentPath(parent);
    if (parent) crumbs.push({ label: parent, path: parentPath || null });
    const menuLabel = screen?.menu?.label || screen?.header?.title || activeModule;
    const form = activeModule === "productDetail" ? getS()?.form || {} : {};
    const detailName = String(form.name || "").trim();
    let lastLabel =
      activeModule === "productDetail" && detailName ? detailName : menuLabel;
    if (lastLabel && !(activeModule === "productDetail" && detailName)) {
      const firstNorm = String(crumbs[0]?.label || "").trim().toLowerCase();
      const parentNorm = String(parent || "").trim().toLowerCase();
      const lastNorm = String(lastLabel).trim().toLowerCase();
      const dupFirst = lastNorm === firstNorm;
      const dupParent = parentNorm && lastNorm === parentNorm;
      if (dupFirst || dupParent) {
        const alias = String(screen?.menu?.breadcrumbAlias || "").trim();
        const title = String(screen?.header?.title || "").trim();
        const titleNorm = title.toLowerCase();
        lastLabel =
          alias
          || (title && titleNorm !== firstNorm && titleNorm !== parentNorm ? title : "")
          || "Startup";
      }
    }
    if (lastLabel) crumbs.push({ label: lastLabel, path: null });
    return crumbs;
  };
  const seedCatalogIfEmpty = async (scr) => {
    await fetchAndBroadcastMergedCatalog(scr, activeModule);
  };

  useEffect(() => {
    const onCatalogUpdated = () => {
      if (activeModule !== "products" && activeModule !== "adminProducts" && activeModule !== "landing") return;
      if (!screen?.defaultCatalog || typeof screen.defaultCatalog !== "string" || !screen.defaultCatalog.trim()) {
        return;
      }
      void fetchAndBroadcastMergedCatalog(screen, activeModule);
    };
    window.addEventListener(CATALOG_UPDATED_EVENT, onCatalogUpdated);
    return () => window.removeEventListener(CATALOG_UPDATED_EVENT, onCatalogUpdated);
  }, [screen, activeModule]);

  useEffect(() => {
    if (activeModule !== "products" && activeModule !== "adminProducts" && activeModule !== "landing") return;
    if (!screen?.defaultCatalog || typeof screen.defaultCatalog !== "string" || !screen.defaultCatalog.trim()) {
      return;
    }
    let debounceTimer;
    const scheduleRefresh = () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        void fetchAndBroadcastMergedCatalog(screen, activeModule);
      }, 500);
    };
    const onVis = () => {
      if (document.visibilityState === "visible") scheduleRefresh();
    };
    window.addEventListener("focus", scheduleRefresh);
    document.addEventListener("visibilitychange", onVis);
    const poll = window.setInterval(() => {
      void fetchAndBroadcastMergedCatalog(screen, activeModule);
    }, 120_000);
    return () => {
      window.clearTimeout(debounceTimer);
      window.removeEventListener("focus", scheduleRefresh);
      document.removeEventListener("visibilitychange", onVis);
      window.clearInterval(poll);
    };
  }, [screen, activeModule]);

  useEffect(() => {
    
    if (!activeModule) return;

    setScreen(activeModule);
    setHeaderNav(null);

    loadScreen(activeModule)
      .then(async (scr) => {
      const isLoginModule = activeModule === "login";
      const role = getCurrentUserRole();
      const allowedRoles = scr.roles || scr.menu?.roles;
      if (!isLoginModule && !isRoleAllowed(role, allowedRoles)) {
        setScr(null);
        setAccessDenied(true);
        setHeaderNav(null);
        return;
      }

      setAccessDenied(false);
      setScr(scr);

      const needsDemoCatalog =
        activeModule === "landing" ||
        activeModule === "products" ||
        activeModule === "adminProducts" ||
        activeModule === "favorites" ||
        (activeModule === "productDetail" && scr.detailSource?.module === "products");
      if (needsDemoCatalog) {
        await seedCatalogIfEmpty(scr);
      }

      if (activeModule === "favorites") {
        const sourceList = Array.isArray(getScreen("products")?.list) ? getScreen("products").list : [];
        const favSet = new Set(readFavoriteIds());
        const favList = sourceList.filter((item) => favSet.has(String(item?.id)));
        setS({ list: favList, errors: {} });
      }
      if (isLoginModule) {
        setHeaderNav(null);
      } else {
        setHeaderNav(findHeaderTopNav(scr));
      }
      if (activeModule === "login") {
        setS({
          form: {
            email: "",
            password: "",
            authError: "",
            authInfo: "",
            helpTitle: "Demo accounts",
            helpLine1: "shopper@nistta.com / shopper123",
            helpLine2: "vendor@nistta.com / vendor123",
          },
          errors: {},
        });
      }
      if (activeModule === "checkout") {
        const formSec = scr.sections?.find((s) => s.type === "form" && s.id === "checkoutForm");
        const dv = formSec?.defaultValues && typeof formSec.defaultValues === "object" ? formSec.defaultValues : {};
        const baseForm = {};
        Object.entries(dv).forEach(([path, value]) => {
          if (!path.startsWith("form.")) return;
          baseForm[path.slice(5)] = value;
        });
        let prePhone = "";
        try {
          prePhone = sessionStorage.getItem("lfx_checkout_phone") || "";
        } catch (e) {
          prePhone = "";
        }
        setS({ form: { ...baseForm, phone: prePhone || baseForm.phone || "" }, errors: {} });
      }
      if (activeModule === "profile") {
        const authUser = getAuthUser();
        const profile = getAccountProfile(authUser?.email) || authUser || {};
        const hasBio = hasPasskeyForEmail(profile?.email);
        setS({
          form: {
            email: profile?.email || "",
            role: profile?.role || "",
            name: profile?.name || "",
            mobile: profile?.mobile || "",
            address: profile?.address || "",
            enableBiometric: hasBio,
            authError: "",
            authInfo: hasBio ? "Fingerprint login is enabled for this account." : "Fingerprint login is not enabled yet.",
          },
          errors: {},
        });
      }
      const source = scr.detailSource;
      if (source?.module && id) {
        const key = source.idKey || "id";
        const selected = findDetailItemFromLists(source.module, id, key);
        const variantDerived = deriveFromVariantGalleryMap(parseVariantGalleryMap(selected?.variantGalleryMap));
        const mergedSelected = selected
          ? {
              ...selected,
              views: getProductViewCount(selected),
              availableColors:
                selected.availableColors && String(selected.availableColors).trim()
                  ? selected.availableColors
                  : variantDerived.availableColors.join(", "),
              colorImageMap:
                selected.colorImageMap && String(selected.colorImageMap).trim()
                  ? selected.colorImageMap
                  : variantDerived.colorImageMap,
              images:
                selected.images && String(selected.images).trim()
                  ? selected.images
                  : variantDerived.images,
            }
          : null;
        const galleryImages = buildGalleryImages(mergedSelected || {});
        setS({
          form: {
            ...(mergedSelected || {}),
            galleryImages,
            specHeading: "SAREE SPECIFICATIONS:",
          },
          selectedItem: mergedSelected,
          editId: mergedSelected?.id ?? null,
          errors: {},
        });
      }

      // Ensure seq on existing rows
      setTimeout(() => {
        const s = getS();
        if (s?.list?.length && !s.list[0]?.seq) {
          setS({ list: s.list.map((r, i) => ({ ...r, seq: i + 1 })) });
        }

        // Run all rules immediately so grid renders filtered data
        scr.sections?.forEach((section) => {
          if (section.rules?.length) {
            runRules(section.rules, section.id);
          }
        });
      }, 50);
    })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[GenericScreen] loadScreen failed:", activeModule, err);
        setAccessDenied(false);
        setScr({
          header: {
            title: "Screen failed to load",
            subtitle: activeModule,
          },
          menu: {
            label: "Error",
            order: 0,
            roles: ["customer", "shopper", "vendor", "partner", "patient"],
          },
          layout: "1-col",
          sections: [
            {
              type: "form",
              id: "loadError",
              layout: { columns: 1 },
              controls: [
                {
                  id: "loadErrorHead",
                  type: "sectionheader",
                  title: `Could not load /assets/screens/${activeModule}.json`,
                  hint: String(err?.message || err || "Unknown error").slice(0, 500),
                  hideLabel: true,
                  colSpan: 1,
                },
              ],
            },
          ],
        });
        setHeaderNav(null);
      });

    const unsub = sub(() => refresh((x) => x + 1));
    return () => {
      unsub();
      setHeaderNav(null);
    };
  }, [activeModule, id, setHeaderNav, location.key]);

  useEffect(() => {
    if (activeModule !== "productDetail" || !screen?.detailSource?.module || !id) return;
    const source = screen.detailSource;
    const selected = findDetailItemFromLists(source.module, id, source.idKey || "id");
    if (!selected) return;
    const currentForm = getS()?.form || {};
    const currentToken = String(currentForm?.id || currentForm?.sku || "").trim().toLowerCase();
    const selectedToken = String(selected?.id || selected?.sku || "").trim().toLowerCase();
    if (currentToken && currentToken === selectedToken) return;
    const variantDerived = deriveFromVariantGalleryMap(parseVariantGalleryMap(selected?.variantGalleryMap));
    const mergedSelected = {
      ...selected,
      views: getProductViewCount(selected),
      availableColors:
        selected.availableColors && String(selected.availableColors).trim()
          ? selected.availableColors
          : variantDerived.availableColors.join(", "),
      colorImageMap:
        selected.colorImageMap && String(selected.colorImageMap).trim()
          ? selected.colorImageMap
          : variantDerived.colorImageMap,
      images:
        selected.images && String(selected.images).trim()
          ? selected.images
          : variantDerived.images,
    };
    setS({
      form: {
        ...mergedSelected,
        galleryImages: buildGalleryImages(mergedSelected),
        specHeading: "SAREE SPECIFICATIONS:",
      },
      selectedItem: mergedSelected,
      editId: mergedSelected?.id ?? null,
      errors: {},
    });
  }, [activeModule, id, screen, rev, location.key]);

  useEffect(() => {
    if (activeModule !== "productDetail" || !id) return;
    const token = String(id || "").trim();
    if (!token) return;
    const existing = getS()?.form || {};
    const currentToken = String(existing?.id || existing?.sku || "").trim().toLowerCase();
    if (currentToken === token.toLowerCase()) return;
    let cancelled = false;
    const bindFromCatalogFallback = async () => {
      try {
        const res = await fetch(`/api/product-media/catalog?v=${Date.now()}`, { cache: "no-store" });
        if (!res.ok) return;
        const body = await res.json().catch(() => ({}));
        const list = Array.isArray(body?.list) ? body.list : [];
        const hit = list.find((item) => {
          const byId = String(item?.id || "").trim();
          const bySku = String(item?.sku || "").trim();
          return byId === token || bySku.toLowerCase() === token.toLowerCase();
        });
        if (!hit || cancelled) return;
        const variantDerived = deriveFromVariantGalleryMap(parseVariantGalleryMap(hit?.variantGalleryMap));
        const mergedSelected = {
          ...hit,
          views: getProductViewCount(hit),
          availableColors:
            hit.availableColors && String(hit.availableColors).trim()
              ? hit.availableColors
              : variantDerived.availableColors.join(", "),
          colorImageMap:
            hit.colorImageMap && String(hit.colorImageMap).trim()
              ? hit.colorImageMap
              : variantDerived.colorImageMap,
          images:
            hit.images && String(hit.images).trim()
              ? hit.images
              : variantDerived.images,
        };
        setS({
          form: {
            ...mergedSelected,
            galleryImages: buildGalleryImages(mergedSelected),
            specHeading: "SAREE SPECIFICATIONS:",
          },
          selectedItem: mergedSelected,
          editId: mergedSelected?.id ?? null,
          errors: {},
        });
      } catch (e) {
        // ignore fallback failure; existing state path still applies
      }
    };
    void bindFromCatalogFallback();
    return () => {
      cancelled = true;
    };
  }, [activeModule, id, rev, location.key]);

  useEffect(() => {
    if (activeModule !== "productDetail") {
      lastViewedTokenRef.current = "";
      return;
    }
    const cur = getS();
    const form = cur?.form || {};
    const token = getProductViewToken(form);
    if (!token || token === lastViewedTokenRef.current) return;
    lastViewedTokenRef.current = token;
    const nextViews = incrementProductViewCount(form);
    const nextForm = { ...form, views: nextViews };
    const nextSelected = { ...(cur?.selectedItem || {}), views: nextViews };
    const nextList = Array.isArray(cur?.list)
      ? cur.list.map((row) => (getProductViewToken(row) === token ? { ...row, views: nextViews } : row))
      : undefined;
    setS({
      form: nextForm,
      selectedItem: nextSelected,
      ...(nextList ? { list: nextList } : {}),
    });
  }, [activeModule, id, rev, location.key]);

  const save = () => {
    const s = getS();
    let list = [...(s.list || [])];
    const nextSeq = (list.length ? Math.max(...list.map((r) => r.seq || 0)) : 0) + 1;
    const existing = list.find((r) => r.id === s.editId);
    const variantDerived = deriveFromVariantGalleryMap(parseVariantGalleryMap(s.form?.variantGalleryMap));
    const nextForm = { ...s.form };
    const formSection = (screen?.sections || []).find((sec) => sec.type === "form" && String(sec.id || "").toLowerCase() === "adminproducts");
    const defaultValues = formSection?.defaultValues && typeof formSection.defaultValues === "object"
      ? formSection.defaultValues
      : {};
    Object.entries(defaultValues).forEach(([path, value]) => {
      if (!path || !path.startsWith("form.")) return;
      const key = path.slice(5);
      const current = nextForm[key];
      const isEmpty = current === undefined || current === null || (typeof current === "string" && current.trim() === "");
      if (isEmpty) nextForm[key] = value;
    });
    const nowIso = new Date().toISOString();
    nextForm.uploadedAt = nextForm.uploadedAt || existing?.uploadedAt || nowIso;
    nextForm.updatedAt = nowIso;
    const jsonTextFields = ["colorImageMap", "variantGalleryMap", "productJson"];
    jsonTextFields.forEach((k) => {
      const v = nextForm[k];
      if (v != null && typeof v === "object") {
        try {
          nextForm[k] = JSON.stringify(v, null, 2);
        } catch (e) {
          /* leave as-is */
        }
      }
    });
    const offerTypeRaw = String(nextForm.offerType || "").trim().toLowerCase();
    const isBuyXGetY = offerTypeRaw === "buy x get y" || offerTypeRaw === "buyxgety";
    const isPercent = offerTypeRaw === "percent";
    const isFlat = offerTypeRaw === "flat";
    const buyQty = Number(nextForm.buyQty);
    const getQty = Number(nextForm.getQty);
    const offerPercent = Number(nextForm.offerPercent);
    const flatDiscount = Number(nextForm.flatDiscount);
    const mrp = Number(nextForm.price);
    const offerEnabled = toBool(nextForm.offerActive);
    if (isBuyXGetY) {
      if (!Number.isFinite(buyQty) || buyQty <= 0) nextForm.buyQty = 2;
      if (!Number.isFinite(getQty) || getQty <= 0) nextForm.getQty = 1;
      nextForm.offerType = "buyXgetY";
      if (offerEnabled && !String(nextForm.offerLabel || "").trim()) {
        nextForm.offerLabel = `BUY ${nextForm.buyQty} GET ${nextForm.getQty}`;
      }
    } else if (isPercent) {
      nextForm.offerType = "percent";
      if (!Number.isFinite(offerPercent) || offerPercent <= 0) nextForm.offerPercent = 10;
      if (offerEnabled && Number.isFinite(mrp) && mrp > 0) {
        const nextOffer = Math.max(0, mrp - (mrp * Number(nextForm.offerPercent)) / 100);
        nextForm.discountPrice = Number(nextOffer.toFixed(2));
        if (!String(nextForm.offerLabel || "").trim()) {
          nextForm.offerLabel = `${Number(nextForm.offerPercent)}% OFF`;
        }
      }
    } else if (isFlat) {
      nextForm.offerType = "flat";
      if (!Number.isFinite(flatDiscount) || flatDiscount <= 0) nextForm.flatDiscount = 500;
      if (offerEnabled && Number.isFinite(mrp) && mrp > 0) {
        const nextOffer = Math.max(0, mrp - Number(nextForm.flatDiscount));
        nextForm.discountPrice = Number(nextOffer.toFixed(2));
        if (!String(nextForm.offerLabel || "").trim()) {
          nextForm.offerLabel = `FLAT ₹${Number(nextForm.flatDiscount)} OFF`;
        }
      }
    } else if (offerTypeRaw === "none" || !offerTypeRaw) {
      nextForm.offerType = "none";
    }
    const stockCountNum = Number(nextForm.stockCount);
    const hasStockCount = Number.isFinite(stockCountNum);
    const stockStatus = String(nextForm.stockStatus || "").toLowerCase();
    const forcedOut = stockStatus === "out of stock";
    if (forcedOut || (hasStockCount && stockCountNum <= 0)) {
      nextForm.outOfStock = true;
      nextForm.stock = "Out of stock";
    } else if (hasStockCount && stockCountNum < 5) {
      nextForm.outOfStock = false;
      nextForm.stock = "Limited stock";
    } else {
      nextForm.outOfStock = false;
      nextForm.stock = "In stock";
    }
    if ((!nextForm.availableColors || !String(nextForm.availableColors).trim()) && variantDerived.availableColors.length) {
      nextForm.availableColors = variantDerived.availableColors.join(", ");
    }
    if ((!nextForm.colorImageMap || !String(nextForm.colorImageMap).trim()) && Object.keys(variantDerived.colorImageMap).length) {
      nextForm.colorImageMap = variantDerived.colorImageMap;
    }
    if ((!nextForm.images || !String(nextForm.images).trim()) && variantDerived.images.length) {
      nextForm.images = variantDerived.images;
    }

    const normalizedSku = String(nextForm.sku || "").trim().toLowerCase();
    const existingBySku = normalizedSku
      ? list.find((r) => String(r?.sku || "").trim().toLowerCase() === normalizedSku)
      : null;
    const targetId = s.editId ?? existingBySku?.id ?? Date.now();
    const targetExisting = existing || existingBySku;
    const item = { id: targetId, seq: targetExisting?.seq || nextSeq, ...nextForm };
    delete item.galleryImages;
    delete item.specHeading;

    if (targetExisting) {
      list = list.map((r) => (r.id === targetId ? item : r));
    } else {
      list.push(item);
    }
    const shouldRetainForm = activeModule === "adminProducts" || activeModule === "adminproducts";
    if (shouldRetainForm) {
      setS({ list, form: { ...item }, editId: item.id, errors: {} });
    } else {
      setS({ list, form: {}, editId: null, errors: {} });
    }
    // Broadcast list to all screens for synchronization
    broadcastList(list);

    if (shouldRetainForm) {
      if (item.sku) {
        persistCatalogItemToServer(item)
          .then(() => notifyCatalogUpdated())
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.warn("[admin] Could not sync product to server (list kept locally; fix error and save again):", e?.message || e);
            // Do not refetch here: GET can return empty/stale and wipe the row we just added.
          });
      } else {
        notifyCatalogUpdated();
      }
    }
  };

  const del = () => {
    const s = getS();
    if (!s.editId) return;
    const selected = (s.list || []).find((r) => r.id === s.editId) || {};
    const selectedSkuRaw = String(selected?.sku || s?.form?.sku || "").trim();
    const selectedSku = selectedSkuRaw.toLowerCase();
    const filteredList = (s.list || []).filter((r) => {
      if (r.id === s.editId) return false;
      if (!selectedSku) return true;
      return String(r?.sku || "").trim().toLowerCase() !== selectedSku;
    });
    setS({ list: filteredList, form: {}, editId: null });
    // Broadcast list to all screens for synchronization
    broadcastList(filteredList);
    if (selectedSkuRaw) {
      deleteCatalogItemFromServer(selectedSkuRaw)
        .then(() => notifyCatalogUpdated())
        .catch((e) => {
          // eslint-disable-next-line no-console
          console.warn("[admin] Could not delete product from server (may reappear on refresh):", e?.message || e);
          notifyCatalogUpdated();
        });
    } else {
      notifyCatalogUpdated();
    }
  };

  const handleAddToCart = () => {
    const form = getS()?.form || {};
    const id = form.id;
    if (id === undefined || id === null || id === "") return;
    if (toBool(form.outOfStock)) {
      setV("form.cartInfo", "This item is out of stock.");
      setTimeout(() => setV("form.cartInfo", ""), 4000);
      return;
    }
    const color = String(form.selectedDetailColor || "").trim() || firstColorFromAvailable(form.availableColors);
    const unitPrice = Number(form.discountPrice);
    const fallbackPrice = Number(form.price);
    const price = Number.isFinite(unitPrice) && unitPrice > 0 ? unitPrice : fallbackPrice;
    addToCartLine({
      productId: id,
      name: form.name || "Product",
      category: form.category || "",
      color,
      unitPrice: Number.isFinite(price) ? price : 0,
      mrp: Number(form.price) || 0,
      image: pickCartImage(form, color),
      qty: 1,
    });
    setV("form.cartInfo", "Added to bag. You can open the cart from the header.");
    setTimeout(() => setV("form.cartInfo", ""), 4000);
  };

  const handlePlaceOrder = () => {
    const scr = screen;
    const checkoutSection = scr?.sections?.find((sec) => sec.type === "form" && sec.id === "checkoutForm");
    const controls = checkoutSection?.controls || [];
    if (!validate(controls)) return;
    clearCartStorage();
    setS({
      form: {
        orderInfo:
          "Thank you! Your order was received. We will contact you to confirm payment and delivery. (Demo checkout — no real charge.)",
      },
      errors: {},
    });
  };

  const handleLogin = () => {
    const s = getS();
    const result = login({
      email: s.form?.email,
      password: s.form?.password,
    });

    if (!result.ok) {
      setS({
        form: {
          ...(s.form || {}),
          authError: result.error || "Login failed",
          authInfo: "",
          password: "",
        },
      });
      return;
    }

    setS({
      form: {
        email: "",
        password: "",
        authError: "",
        authInfo: "",
        helpTitle: "Demo accounts",
        helpLine1: "shopper@nistta.com / shopper123",
        helpLine2: "vendor@nistta.com / vendor123",
      },
      errors: {},
    });

    const nextFromState = location.state?.from?.pathname;
    const next = nextFromState || getDefaultRouteForRole(result.user.role);
    clearSessionScopedUI();
    setS({
      form: {
        email: "",
        password: "",
        authError: "",
        authInfo: `Signed in as ${result.user.role} -> redirecting to ${next}`,
        helpTitle: "Demo accounts",
        helpLine1: "shopper@nistta.com / shopper123",
        helpLine2: "vendor@nistta.com / vendor123",
      },
      errors: {},
    });
    setTimeout(() => navigate(next, { replace: true }), 500);
  };

  const handleBiometricLogin = async () => {
    const s = getS();
    const result = await loginWithPasskey({});
    if (!result.ok) {
      setS({
        form: {
          ...(s.form || {}),
          authError: result.error || "Fingerprint login failed",
          authInfo: "",
        },
      });
      return;
    }
    const nextFromState = location.state?.from?.pathname;
    const next = nextFromState || getDefaultRouteForRole(result.user.role);
    clearSessionScopedUI();
    setS({
      form: {
        ...(s.form || {}),
        authError: "",
        authInfo: "Fingerprint verified. Signing you in...",
      },
      errors: {},
    });
    setTimeout(() => navigate(next, { replace: true }), 450);
  };

  const handleSignup = async () => {
    const s = getS();
    const role = activeModule === "vendorSignup" ? "vendor" : "shopper";
    const result = signup({
      email: s.form?.email,
      password: s.form?.password,
      name: s.form?.name,
      mobile: s.form?.mobile,
      address: s.form?.address,
      role,
    });
    if (!result.ok) {
      setS({
        form: {
          ...(s.form || {}),
          authError: result.error || "Signup failed",
          authInfo: "",
        },
      });
      return;
    }
    const next = getDefaultRouteForRole(result.user.role);
    let bioInfo = "";
    if (toBool(s.form?.enableBiometric)) {
      const passkey = await registerPasskeyForEmail({ email: result.user.email, name: result.user.name });
      bioInfo = passkey.ok
        ? " Fingerprint login enabled for this account."
        : ` Account created. Fingerprint setup skipped: ${passkey.error || "not available"}`;
    }
    clearSessionScopedUI();
    setS({
      form: {
        ...(s.form || {}),
        authError: "",
        authInfo: `Account created for ${result.user.role}. Redirecting...${bioInfo}`,
      },
      errors: {},
    });
    setTimeout(() => navigate(next, { replace: true }), 500);
  };

  const handleProfileSave = () => {
    const s = getS();
    const authUser = getAuthUser();
    const email = s.form?.email || authUser?.email;
    const result = updateAccountProfile({
      email,
      name: s.form?.name,
      mobile: s.form?.mobile,
      address: s.form?.address,
    });
    if (!result.ok) {
      setS({
        form: {
          ...(s.form || {}),
          authError: result.error || "Could not update profile.",
          authInfo: "",
        },
      });
      return;
    }
    const refreshed = getAccountProfile(email) || {};
    const normalizedEmail = String(email || "").trim().toLowerCase();
    const wantsBiometric = toBool(s.form?.enableBiometric);
    const hasBiometric = hasPasskeyForEmail(normalizedEmail);
    const done = (authError, authInfo, enableBiometric) => {
      setS({
        form: {
          ...(s.form || {}),
          name: refreshed.name || s.form?.name || "",
          mobile: refreshed.mobile || s.form?.mobile || "",
          address: refreshed.address || s.form?.address || "",
          enableBiometric,
          authError,
          authInfo,
        },
        errors: {},
      });
    };
    if (wantsBiometric && !hasBiometric) {
      registerPasskeyForEmail({ email: normalizedEmail, name: refreshed.name || s.form?.name || "" })
        .then((passkey) => {
          if (!passkey.ok) {
            done(passkey.error || "Profile saved, but fingerprint setup failed.", "", false);
            return;
          }
          done("", "Profile updated and fingerprint login enabled.", true);
        })
        .catch(() => done("Profile saved, but fingerprint setup failed.", "", false));
      return;
    }
    if (!wantsBiometric && hasBiometric) {
      const removed = removePasskeyForEmail(normalizedEmail);
      if (!removed.ok) {
        done(removed.error || "Profile updated, but could not disable fingerprint login.", "", true);
        return;
      }
      done("", "Profile updated and fingerprint login disabled.", false);
      return;
    }
    done("", "Profile updated successfully.", wantsBiometric);
  };

  const handleProfileRegisterBiometric = async () => {
    const s = getS();
    const authUser = getAuthUser();
    const email = s.form?.email || authUser?.email;
    const name = s.form?.name || authUser?.name || "";
    const result = await registerPasskeyForEmail({ email, name });
    setS({
      form: {
        ...(s.form || {}),
        authError: result.ok ? "" : (result.error || "Could not enable fingerprint login."),
        authInfo: result.ok ? "Fingerprint login enabled for this account." : "",
      },
      errors: {},
    });
  };

  if (activeModule === "login" && isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  if (!accessDenied && !screen && activeModule && activeModule !== "login") {
    return (
      <div className="lfx-page">
        <div className="lfx-loading-state" role="status" aria-live="polite" style={{ padding: "2rem" }}>
          <span className="lfx-spinner" aria-hidden />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  return (
    accessDenied ? (
      <>
        <PageHeader
          title="Access Restricted"
          subtitle="You do not have permission to view this screen."
          breadcrumbs={[{ label: "Home", path: "/" }, { label: "Access Restricted", path: null }]}
        />
        <div className="lfx-card">
          <div className="lfx-card-body">
            <div className="lfx-empty-state">
              <span>Please switch to an authorized user role to continue.</span>
            </div>
          </div>
        </div>
      </>
    ) : (
      screen && (
        activeModule === "login" ? (
          <div className="lfx-auth-page">
            <div className="lfx-auth-layout">
              <section className="lfx-auth-brand-panel">
                <div className="lfx-auth-brand-overlay">
                  <span className="lfx-auth-brand-kicker">NISTTA | Indian Heritage Weaves</span>
                  <h1 className="lfx-auth-brand-title">Tradition in Every Drape.</h1>
                  <p className="lfx-auth-brand-copy">
                    Explore handpicked sarees inspired by Banarasi, Kanjivaram, Chanderi, and modern festive collections.
                  </p>
                  <div className="lfx-auth-brand-points">
                    <span>Authentic craftsmanship</span>
                    <span>Curated festive collections</span>
                    <span>Trusted seller network</span>
                  </div>
                </div>
              </section>
              <div className="lfx-auth-shell">
                <PageHeader
                  title={screen?.header?.title}
                  subtitle={screen?.header?.subtitle}
                  breadcrumbs={buildBreadcrumbs()}
                />
                <LayoutEngine
                  screen={screen}
                  actions={{
                    save,
                    delete: del,
                    login: handleLogin,
                    loginBiometric: handleBiometricLogin,
                    signup: handleSignup,
                    saveProfile: handleProfileSave,
                    registerBiometric: handleProfileRegisterBiometric,
                    addToCart: handleAddToCart,
                    placeOrder: handlePlaceOrder,
                  }}
                  moduleName={activeModule}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            {activeModule === "productDetail" ? (
              (() => {
                const form = getS()?.form || {};
                const currentId = String(form.id || "");
                const favIds = readFavoriteIds();
                const isFav = currentId ? favIds.includes(currentId) : false;
                const title = form.name || screen?.header?.title || "Product Details";
                const category = form.category || "—";
                const type = form.workType || form.pattern || form.fabric || "—";
                const offer = formatInr(form.discountPrice) || "—";
                const mrp = formatInr(form.price) || "—";
                const views = Number(form.views);
                const loggedIn = isAuthenticated();
                const canSaveFavorite = loggedIn && getCurrentUserRole() === "shopper";
                const toggleFavorite = () => {
                  if (!currentId) return;
                  const nextIds = isFav ? favIds.filter((id) => id !== currentId) : [...favIds, currentId];
                  writeFavoriteIds(nextIds);
                  refresh((x) => x + 1);
                };
                return (
                  <>
                    <PageHeader
                      title=""
                      subtitle=""
                      breadcrumbs={buildBreadcrumbs()}
                      className="lfx-page-header--compact-crumbs"
                    />
                    <div className="lfx-detail-sticky-head">
                      <div className="lfx-detail-sticky-main">
                        <div className="lfx-detail-sticky-title-row">
                          <h1 className="lfx-detail-sticky-title">{title}</h1>
                          <button
                            type="button"
                            className="lfx-btn secondary sm lfx-detail-inline-back"
                            onClick={() => navigate(-1)}
                          >
                            Back
                          </button>
                        </div>
                        <div className="lfx-detail-sticky-meta">
                          <span>{category}</span>
                          <span>{type}</span>
                          <span>
                            <strong>{offer}</strong> <em>{mrp}</em>
                          </span>
                          <span>{Number.isFinite(views) ? `${views} views` : "0 views"}</span>
                        </div>
                      </div>
                      <div className="lfx-detail-sticky-actions">
                        {canSaveFavorite ? (
                          <button
                            className={`lfx-btn secondary sm lfx-detail-action-btn ${isFav ? "active" : ""}`}
                            onClick={toggleFavorite}
                            title={isFav ? "Remove from favorites" : "Save to favorites"}
                          >
                            <Heart size={14} fill={isFav ? "currentColor" : "none"} />
                            {isFav ? "Saved" : "Save"}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </>
                );
              })()
            ) : (
              <PageHeader
                title={screen?.header?.title}
                subtitle={screen?.header?.subtitle}
                breadcrumbs={buildBreadcrumbs()}
              />
            )}
            <LayoutEngine
              screen={screen}
              actions={{
                save,
                delete: del,
                login: handleLogin,
                loginBiometric: handleBiometricLogin,
                signup: handleSignup,
                saveProfile: handleProfileSave,
                registerBiometric: handleProfileRegisterBiometric,
                addToCart: handleAddToCart,
                placeOrder: handlePlaceOrder,
              }}
              moduleName={activeModule}
            />
            {activeModule === "productDetail" ? (
              <a
                href={buildWhatsappUrl()}
                target="_blank"
                rel="noreferrer"
                className="lfx-whatsapp-fab"
                title="Enquire on WhatsApp"
              >
                <MessageCircle size={20} />
              </a>
            ) : null}
          </>
        )
      )
    )
  );
}
